import {AxiosResponse} from "axios";
import api from "../util/api";
import {Lifetime} from "../util/Lifetime";

export class RestResource<Entities> {
    protected readonly api = api;
    protected readonly path: string;

    constructor(path: string) {
        this.path = path;
    }

    async getAll<QueryParams = {}>(queryParams?: QueryParams): Promise<Entities> {
        const response: AxiosResponse<Entities> = await this.api.get(this.path, {
            params: queryParams
        });
        return response.data;
    }
}

export class CrudRestResource<Form, Entity, Entities = Entity[]> extends RestResource<Entities>{
    private readonly idKey: (entity: Entity) => any;

    constructor(path: string, idKey: keyof Entity | ((entity: Entity) => any)) {
        super(path);
        this.idKey = idKey instanceof Function ? idKey : (entity: Entity) => entity[idKey];
    }

    async create(form: Form) {
        const response: AxiosResponse<Entity> = await this.api.post(this.path, form);
        return response.data;
    }

    async getOne(id: string | number): Promise<Entity> {
        const response: AxiosResponse<Entity> = await this.api.get(`${this.path}/${id}`);
        return response.data;
    }

    async uploadFiles(files: File[], lifetime: Lifetime = new Lifetime(), onUploadProgress?: (progress: number) => void) {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('file', file);
        });
        const abortController = new AbortController();
        const unsubscribe = lifetime.onTermination(() => abortController.abort());

        try {
            const response: AxiosResponse<Entities> = await api.post(this.path, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                signal: abortController.signal,
                onUploadProgress: event => {
                    const progress = event.progress ?? (event.total ? event.loaded / event.total : undefined);
                    if (progress !== undefined) {
                        onUploadProgress?.(Math.max(0, Math.min(100, progress * 100)));
                    }
                },
            });

            return response.data;
        } finally {
            unsubscribe();
        }
    }

    async update(entity: Entity, form: Form) {
        const response: AxiosResponse<Entity> = await this.api.patch(`${this.path}/${this.idKey(entity)}`, form);
        return response.data;
    }

    async updateCustom<CustomEntity>(entity: Entity, form: Form) {
        const response: AxiosResponse<CustomEntity> = await this.api.patch(`${this.path}/${this.idKey(entity)}`, form);
        return response.data;
    }

    async delete(entity: Entity) {
        await this.api.delete(`${this.path}/${this.idKey(entity)}`);
    }

    subresource<SubForm, SubEntity, SubEntities = SubEntity[]>(subPath: string, idKey: keyof SubEntity | ((entity: SubEntity) => any)): (entity: Entity) => CrudRestResource<SubForm, SubEntity, SubEntities>  {
        return (entity: Entity) => new CrudRestResource<SubForm, SubEntity, SubEntities>(`${this.path}/${this.idKey(entity)}/${subPath}`, idKey);
    }
}
